import { useLoaderData } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

// Services
import { getFooter, type FooterType } from '@/services/footer/footerService';
import { getHeader, type HeaderType } from '@/services/header/headerService';
import { getHomePage, type HomePageType } from '@/services/home-page/homePageService';
import { getLatestNews } from '@/services/news/newsService';

// Components
import Header from '@/components/organisms/Header/Header';
import Footer from '@/components/organisms/Footer/Footer';
import { PageBlocks } from '@/components/blocks/Blocks';

// Utils
import { getLocale } from '@/utils/localeSlug';
import { buildSEOUrl } from '@/utils/seo';

// Styles
import styles from './Home.module.css';

export async function homePageLoader({ request }: { request: Request }) {
  const url = new URL(request.url);

  const locale = getLocale(url);

  const [header, footer, homePage] = await Promise.all([
    getHeader(locale),
    getFooter(locale),
    getHomePage(locale),
  ]);

  // eslint-disable-next-line no-underscore-dangle
  const hasNewsSection = homePage.blocks.find(block => block.__component === 'home.section-actualite');
  let latestNews = null;
  if (hasNewsSection) {
    latestNews = await getLatestNews(locale);
  }
  
  return {
    header,
    footer,
    homePage,
    latestNews
  };
}

function Home() {
  const baseUrl = import.meta.env.VITE_DOMAIN;
  // API Calls
  const data = useLoaderData() as Awaited<ReturnType<typeof homePageLoader>>;

  if (!data) return null;

  const { header, footer, homePage } = data;

  if (!homePage) return null;

  return (
    <main>
      <Helmet htmlAttributes={{ lang: homePage.locale }}>
        <title>{homePage.seo.metaTitle}</title>
        <meta name="description" content={homePage.seo.metaDescription} />
        <link 
          rel='canonical'
          href={homePage.seo.canonicalLink ?? buildSEOUrl(baseUrl, homePage.locale, homePage.slug)}
        />
        <link
          rel='alternate' hrefLang={homePage.locale}
          href={buildSEOUrl(baseUrl, homePage.locale)}
        />
        <link
          rel='alternate' hrefLang='x-default'
          href={buildSEOUrl(baseUrl, 'fr-FR')}
        />
        {
          homePage.localizations.map((localization) => (
            <link
              key={localization.documentId}
              rel='alternate' hrefLang={localization.locale}
              href={buildSEOUrl(baseUrl, localization.locale)}
            />
          ))
        }
      </Helmet>
      <Header
        color='dark'
        header={header as HeaderType}
      />

      <div className={styles.home}>
        <PageBlocks blocks={(homePage as HomePageType).blocks} />
      </div>
      
      <Footer
        footer={footer as FooterType}
      />
    </main>
  );
}

export default Home;